import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import Snackbar from './Snackbar';
import './Roles.css';

const CustomSearchDropdown = ({
  label,
  value,
  onChange,
  options,
  placeholder,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="custom-dropdown-container">
      {label && <label className="custom-dropdown-label">{label}</label>}

      <div
        className="custom-dropdown-box"
        onClick={() => setOpen(!open)}
      >
        <span className="dropdown-value">
          {value ? options.find((o) => o.value === value)?.label : placeholder}
        </span>
        <span className="dropdown-arrow">▼</span>
      </div>

      {open && (
        <div className="custom-dropdown-panel">
          <input
            type="text"
            value={search}
            className="dropdown-search-input"
            placeholder="Search..."
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="dropdown-options-list">
            {filtered.length > 0 ? (
              filtered.map((opt) => (
                <div
                  key={opt.value}
                  className={`dropdown-option ${
                    opt.value === value ? "selected" : ""
                  }`}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  {opt.label}
                </div>
              ))
            ) : (
              <div className="dropdown-no-results">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DepartmentManagement = () => {
  const navigate = useNavigate();

  const [managers, setManagers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' });
  const showSnackbar = (msg, type = 'success') => setSnackbar({ open: true, message: msg, type });

  // Assign Department Modal states
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [availableDepartments, setAvailableDepartments] = useState([]);

  // Edit Department Modal states
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editManager, setEditManager] = useState(null);
  const [editDepartmentId, setEditDepartmentId] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Delete Modal states
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteManager, setDeleteManager] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch managers
        const managersResponse = await apiService.fetchData('get-managers-department');
        if (managersResponse && managersResponse.data) {
          const formattedData = managersResponse.data.map(manager => ({
            id: manager.manager_id,
            managerName: manager.manager_name || manager.manager_email,
            managerEmail: manager.manager_email,
            departmentName: manager.department_name,
            departmentId: manager.department_id,
            headEmail: manager.head_email

          }));
          setManagers(formattedData);
          setFiltered(formattedData);
        }
        
        // Fetch all departments
        const deptResponse = await apiService.fetchData('metadata?type=departments');
        if (deptResponse && deptResponse.data) {
          const allDepts = deptResponse.data.map(dept => ({
            id: dept.DepartmentID,
            name: dept.DepartmentName
          }));
          setAvailableDepartments(allDepts);
        }
        
      } catch (err) {
        console.error('Error fetching data:', err);
        showSnackbar('Failed to fetch data', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFiltered(managers);
    } else {
      const result = managers.filter(m =>
        (m.managerEmail || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (m.departmentName || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFiltered(result);
    }
    setCurrentPage(1);
  }, [searchQuery, managers]);

  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  return (
    <div className="roles-container">
      {/* Header */}
      <div className="roles-header-section">
        <button className="roles-back-btn" onClick={() => navigate(-1)}>
          ← Back To Home
        </button>
        <div className="roles-title-wrapper">
          <h1 className="roles-page-title">Department Management</h1>
          <p className="roles-page-subtitle">Manage managers and their departments</p>
        </div>
      </div>

      {/* Search */}
      <div className="roles-search-section">
        <div className="roles-search-box">
          <input
            type="text"
            placeholder="Search by manager email or department..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="roles-table-wrapper">
        {loading ? (
          <div className="loading-container" style={{ textAlign: 'center', padding: '20px' }}>
            <p>Loading managers...</p>
          </div>
        ) : (
        <table className="roles-table">
          <thead>
            <tr>
              <th style={{ width: '25%' }}>Manager Mail</th>
              <th style={{ width: '25%', position: 'relative' }}>
                Department Name
               
              </th>
              <th style={{ width: '25%' }}>Reporting Manager</th>
              <th style={{ width: '25%' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((manager, index) => (
              <tr key={`${manager.id}-${manager.managerEmail}-${index}`} className="roles-table-row">
                <td><strong>{manager.managerEmail}</strong></td>
                <td><span className="roles-dept-badge">{manager.departmentName}</span></td>
                <td><strong>{manager.headEmail || 'N/A'}</strong></td>
                <td className="roles-actions">
                  <button
                    onClick={() => {
                      setEditManager(manager);
                      setEditDepartmentId(manager.departmentId);
                      setIsEditOpen(true);
                    }}
                    className="roles-icon-btn roles-edit-btn"
                    title="Edit Manager Department"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedManager(manager);
                      setSelectedDepartmentId('');
                      setIsAssignOpen(true);
                    }}
                    className="roles-icon-btn roles-edit-btn"
                    title="Assign Department"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M12 5v14m-7-7h14" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      setDeleteManager(manager);
                      setIsDeleteOpen(true);
                    }}
                    className="roles-icon-btn roles-delete-btn"
                    title="Remove Manager"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}

        {/* Pagination */}
        {!loading && (
        <div className="roles-pagination">
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="roles-page-btn">
            ← Previous
          </button>
          <span className="roles-page-info">Page {currentPage} of {totalPages}</span>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="roles-page-btn">
            Next →
          </button>
        </div>
        )}
      </div>

      {/* Assign Department Modal */}
      {isAssignOpen && (
        <div className="roles-modal-overlay" onClick={() => !isAssigning && setIsAssignOpen(false)}>
          <div className="roles-modal" onClick={e => e.stopPropagation()}>
            <h3 className="roles-modal-title">Assign Department</h3>
            <div style={{ padding: '20px' }}>
              <p style={{ marginBottom: '20px', color: '#64748b', fontSize: '14px' }}>
                Assign <strong>{selectedManager?.managerEmail}</strong> to a department
              </p>
              <CustomSearchDropdown
                label="Select Department"
                placeholder="Choose Department"
                value={selectedDepartmentId}
                onChange={(v) => setSelectedDepartmentId(v)}
                options={availableDepartments.map((dept) => ({
                  value: dept.id,
                  label: dept.name,
                }))}
              />
            </div>
            <div className="roles-modal-actions">
              <button 
                onClick={() => setIsAssignOpen(false)} 
                className="roles-btn-cancel"
                disabled={isAssigning}
              >
                Cancel
              </button>
              <button 
                onClick={async () => {
                  if (!selectedDepartmentId) {
                    showSnackbar('Please select a department', 'error');
                    return;
                  }
                  try {
                    setIsAssigning(true);
                    const payload = {
                      manager_id: selectedManager.id,
                      department_id: parseInt(selectedDepartmentId)
                    };
                    await apiService.postData('assign-department-manager', payload);
                    showSnackbar('Department assigned successfully', 'success');
                    setIsAssignOpen(false);
                    // Refresh data
                    const response = await apiService.fetchData('get-managers-department');
                    if (response && response.data) {
                      const formattedData = response.data.map(manager => ({
                        id: manager.manager_id,
                        managerName: manager.manager_name || manager.manager_email,
                        managerEmail: manager.manager_email,
                        departmentName: manager.department_name,
                        departmentId: manager.department_id
                      }));
                      setManagers(formattedData);
                      setFiltered(formattedData);
                    }
                  } catch (err) {
                    console.error('Error assigning department:', err);
                    showSnackbar('Failed to assign department', 'error');
                  } finally {
                    setIsAssigning(false);
                  }
                }}
                className="roles-btn-gradient"
                disabled={isAssigning}
              >
                {isAssigning ? 'Assigning...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Department Modal */}
      {isEditOpen && (
        <div className="roles-modal-overlay" onClick={() => !isUpdating && setIsEditOpen(false)}>
          <div className="roles-modal" onClick={e => e.stopPropagation()}>
            <h3 className="roles-modal-title">Edit Department</h3>
            <div style={{ padding: '20px' }}>
              <p style={{ marginBottom: '20px', color: '#64748b', fontSize: '14px' }}>
                Change department for <strong>{editManager?.managerEmail}</strong>
              </p>
              <CustomSearchDropdown
                label="Select Department"
                placeholder="Choose Department"
                value={editDepartmentId}
                onChange={(v) => setEditDepartmentId(v)}
                options={availableDepartments.map((dept) => ({
                  value: dept.id,
                  label: dept.name,
                }))}
              />
            </div>
            <div className="roles-modal-actions">
              <button 
                onClick={() => setIsEditOpen(false)} 
                className="roles-btn-cancel"
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button 
                onClick={async () => {
                  if (!editDepartmentId) {
                    showSnackbar('Please select a department', 'error');
                    return;
                  }
                  try {
                    setIsUpdating(true);
                    const endpoint = `manage-manager-department?manager_id=${editManager.id}&department_id=${editDepartmentId}&old_department_id=${editManager.departmentId}`;
                    await apiService.putData(endpoint, {});
                    showSnackbar('Department updated successfully', 'success');
                    setIsEditOpen(false);
                    // Refresh data
                    const response = await apiService.fetchData('get-managers-department');
                    if (response && response.data) {
                      const formattedData = response.data.map(manager => ({
                        id: manager.manager_id,
                        managerName: manager.manager_name || manager.manager_email,
                        managerEmail: manager.manager_email,
                        departmentName: manager.department_name,
                        departmentId: manager.department_id
                      }));
                      setManagers(formattedData);
                      setFiltered(formattedData);
                    }
                  } catch (err) {
                    console.error('Error updating department:', err);
                    showSnackbar('Failed to update department', 'error');
                  } finally {
                    setIsUpdating(false);
                  }
                }}
                className="roles-btn-gradient"
                disabled={isUpdating}
              >
                {isUpdating ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteOpen && (
        <div className="roles-modal-overlay" onClick={() => !isDeleting && setIsDeleteOpen(false)}>
          <div className="roles-modal" onClick={e => e.stopPropagation()}>
            <h3 className="roles-modal-title">Remove Manager?</h3>
            <p className="role-delete-des" style={{padding:'20px'}}>
              Are you sure you want to remove <strong>{deleteManager?.managerEmail}</strong> from <strong>{deleteManager?.departmentName}</strong>?
            </p>
            <div className="roles-modal-actions">
              <button 
                onClick={() => setIsDeleteOpen(false)} 
                className="roles-btn-cancel"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                onClick={async () => {
                  try {
                    setIsDeleting(true);
                    const endpoint = `manage-manager-department?manager_id=${deleteManager.id}&department_id=${deleteManager.departmentId}`;
                    await apiService.deleteData(endpoint);
                    showSnackbar('Manager removed successfully', 'success');
                    setIsDeleteOpen(false);
                    // Refresh data
                    const response = await apiService.fetchData('get-managers-department');
                    if (response && response.data) {
                      const formattedData = response.data.map(manager => ({
                        id: manager.manager_id,
                        managerName: manager.manager_name || manager.manager_email,
                        managerEmail: manager.manager_email,
                        departmentName: manager.department_name,
                        departmentId: manager.department_id
                      }));
                      setManagers(formattedData);
                      setFiltered(formattedData);
                    }
                  } catch (err) {
                    console.error('Error removing manager:', err);
                    showSnackbar('Failed to remove manager', 'error');
                  } finally {
                    setIsDeleting(false);
                  }
                }}
                className="roles-btn-danger"
                disabled={isDeleting}
              >
                {isDeleting ? 'Removing...' : 'Yes, Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {snackbar.message && snackbar.message.trim() && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          isOpen={snackbar.open}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          duration={3000}
        />
      )}
    </div>
  );
};

export default DepartmentManagement;